import React, { useCallback, useEffect, useRef, useState } from 'react';

import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import {
  Alert,
  Box,
  Button,
  Card,
  CardMedia,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Typography,
} from '@mui/material';

import { Experiences } from '@luxuryescapes/contract-svc-experience';

import { curationExperience, getExperienceOfferById, uploadOfferImage } from '~/services/ExperiencesService';

import { resolveOfferImageUrl } from './umbrellaUtils';

interface Props {
  open: boolean;
  onClose: () => void;
  childId: string;
  childTitle: string;
  brand: string;
  /** IDs of all sibling children in the umbrella group (for aggregating images) */
  siblingIds: string[];
  onImageUpdated: () => void;
}

type OfferImage = Experiences.Image;

type AggregatedImage = {
  image: OfferImage;
  offerTitle: string;
  offerId: string;
};

export default function UmbrellaChildImagePicker({
  open,
  onClose,
  childId,
  childTitle,
  brand,
  siblingIds,
  onImageUpdated,
}: Props) {
  const [aggregatedImages, setAggregatedImages] = useState<AggregatedImage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedImageId, setSelectedImageId] = useState<string | number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchAllImages = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const responses = await Promise.allSettled(
        siblingIds.map((id) => getExperienceOfferById({ id, brand, curationData: true })),
      );

      const allImages: AggregatedImage[] = [];

      responses.forEach((res, idx) => {
        if (res.status !== 'fulfilled') return;
        const offer = res.value.result;
        const offerId = siblingIds[idx];

        if (offerId === childId) {
          const hero = offer.images?.find((img) => img.isHero);
          if (hero) setSelectedImageId(hero.id);
        }

        for (const img of offer.images ?? []) {
          allImages.push({
            image: img,
            offerTitle: offer.title ?? offerId,
            offerId,
          });
        }
      });

      const failedCount = responses.filter((r) => r.status === 'rejected').length;
      if (failedCount > 0) {
        setError(`Some offers failed to load (${failedCount}/${responses.length}). Showing available images.`);
      }

      setAggregatedImages(allImages);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load images');
    } finally {
      setIsLoading(false);
    }
  }, [siblingIds, childId, brand]);

  useEffect(() => {
    if (!open) return;
    void fetchAllImages();
  }, [open, fetchAllImages]);

  const handleClose = useCallback(() => {
    setAggregatedImages([]);
    setSelectedImageId(null);
    setError(null);
    onClose();
  }, [onClose]);

  const handleSelectHero = useCallback(
    async (imageId: string | number, sourceOfferId: string) => {
      setIsSaving(true);
      setError(null);
      try {
        const latest = await getExperienceOfferById({ id: childId, brand, curationData: true });
        const targetImages = latest.result.images ?? [];
        const status = latest.result.curationStatus as string | undefined;
        const imageExistsInTarget = targetImages.some((img) => String(img.id) === String(imageId));

        if (imageExistsInTarget) {
          await curationExperience(childId, brand, {
            ...(status != null ? { curationStatus: status } : {}),
            heroImageId: String(imageId),
          });
        } else {
          // Copy sibling image to target child before setting as hero
          const sourceImage = aggregatedImages.find(
            (a) => String(a.image.id) === String(imageId) && a.offerId === sourceOfferId,
          );
          if (sourceImage) {
            const mergedImages = [...targetImages, sourceImage.image];
            await curationExperience(childId, brand, {
              ...(status != null ? { curationStatus: status } : {}),
              images: mergedImages,
              heroImageId: String(imageId),
            });
          } else {
            setError('Could not find the selected image. Please try again.');
            return;
          }
        }

        onImageUpdated();
        handleClose();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to set hero image');
      } finally {
        setIsSaving(false);
      }
    },
    [childId, brand, aggregatedImages, onImageUpdated, handleClose],
  );

  const handleUpload = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0) return;
      setIsUploading(true);
      setError(null);
      try {
        const latest = await getExperienceOfferById({ id: childId, brand, curationData: true });
        const existing = latest.result.images ?? [];
        const status = latest.result.curationStatus as string | undefined;

        const uploaded = await uploadOfferImage(Array.from(files));
        const newImages: OfferImage[] = uploaded.map((img) => ({
          id: img.id,
          urls: [{ url: img.url, width: 0, height: 0 }],
          isHero: false,
          description: undefined,
        }));

        const allImages = [...existing, ...newImages];
        await curationExperience(childId, brand, {
          ...(status != null ? { curationStatus: status } : {}),
          images: allImages,
          heroImageId: String(newImages[0].id),
        });
        onImageUpdated();
        handleClose();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to upload image');
      } finally {
        setIsUploading(false);
      }
    },
    [childId, brand, onImageUpdated, handleClose],
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Select Hero Image: {childTitle}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing={2}
          sx={{
            mb: 3,
            p: 2,
            border: '2px dashed',
            borderColor: 'divider',
            borderRadius: 1,
            cursor: isLoading ? 'not-allowed' : 'pointer',
            opacity: isLoading ? 0.5 : 1,
            pointerEvents: isLoading ? 'none' : 'auto',
          }}
          onClick={() => !isLoading && fileInputRef.current?.click()}
        >
          {isUploading ? (
            <CircularProgress size={24} />
          ) : (
            <>
              <CloudUploadIcon color="action" />
              <Typography variant="body2" color="text.secondary">
                Click to upload a new image
              </Typography>
            </>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => {
              void handleUpload(e.target.files);
              e.target.value = '';
            }}
          />
        </Stack>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : aggregatedImages.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            No existing images. Upload one above.
          </Typography>
        ) : (
          <Grid container spacing={2}>
            {aggregatedImages.map(({ image: img, offerTitle, offerId }) => (
              <Grid size={{ xs: 6, sm: 4, md: 3 }} key={`${offerId}-${img.id}`}>
                <Card
                  sx={{
                    cursor: isSaving ? 'wait' : 'pointer',
                    border: '2px solid',
                    borderColor: String(selectedImageId) === String(img.id) ? 'primary.main' : 'transparent',
                    '&:hover': { borderColor: 'primary.light' },
                    position: 'relative',
                    opacity: isSaving ? 0.6 : 1,
                  }}
                  onClick={() => !isSaving && handleSelectHero(img.id, offerId)}
                >
                  <CardMedia
                    component="img"
                    image={resolveOfferImageUrl(img, 400, 300)}
                    alt={offerTitle}
                    sx={{ height: 120, objectFit: 'cover' }}
                  />
                  <Box sx={{ p: 1 }}>
                    <Typography variant="caption" noWrap>
                      {offerTitle}
                    </Typography>
                  </Box>
                  {img.isHero && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        bgcolor: 'success.main',
                        color: 'white',
                        px: 0.5,
                        borderRadius: 0.5,
                        fontSize: '0.65rem',
                        fontWeight: 600,
                      }}
                    >
                      HERO
                    </Box>
                  )}
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSaving || isUploading}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
